import { useState, FormEvent } from "react";
import ErrorIcon from "../../assets/Error_Purple.svg"
import styles from "./Login.module.css"
import { detectLoginProvider } from "../../api";

const ERROR_MESSAGES: Record<string, string> = {
    unknown_domain: "We couldn't match that email to a sign-in method. Please contact Guide Team at GuideTeam@telushealth.com.",
    invalid_email: "Please enter a valid email address.",
    pingid_not_enabled: "PingID sign-in isn't available right now. Please contact Guide Team at GuideTeam@telushealth.com.",
};

// Backend OAuth callback failure codes (backend/auth/pingid_routes.py) get a
// single generic message - the specifics are for the server logs, not the user.
const OAUTH_FAILURE_MESSAGE = "We couldn't complete PingID sign-in. Please try again, or contact Guide Team at GuideTeam@telushealth.com if the issue persists.";

type LoginProps = {
    // Populated when we're redirected back here after a failed OAuth round-trip
    // (see AuthCallback.tsx), so the user can retry without losing context.
    initialError?: string;
};

export const Login = ({ initialError }: LoginProps) => {
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(initialError ? OAUTH_FAILURE_MESSAGE : null);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const result = await detectLoginProvider(trimmedEmail);
            if (result.redirect_url) {
                window.location.href = result.redirect_url;
                return;
            }
            setError(ERROR_MESSAGES[result.error ?? ""] ?? ERROR_MESSAGES.unknown_domain);
        } catch {
            setError(ERROR_MESSAGES.unknown_domain);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.loginContainer}>
            <div>
                <img className={styles.loginIcon} src={ErrorIcon} alt="" aria-hidden="true" />
            </div>

            <h1 className={styles.loginTitle}>Sign in to continue</h1>
            <p className={styles.loginSubtitle}>Enter your work email and we'll take you to the right sign-in page.</p>

            <form className={styles.loginForm} onSubmit={handleSubmit}>
                <input
                    className={styles.loginInput}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="name@telus.com"
                    aria-label="Work email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                />
                {error && <p className={styles.loginError} role="alert">{error}</p>}
                <button className={styles.loginButton} type="submit" disabled={isSubmitting || !email.trim()}>
                    {isSubmitting ? "Redirecting..." : "Continue"}
                </button>
            </form>

            <p className={styles.loginFooter}>If the issue persists, contact Guide Team at GuideTeam@telushealth.com</p>
        </div>
    );
};
